import type { RatioCard } from '../../../data/ServerApiClient'
// Reuse the canonical formatters so the comparison table renders identically to the tiles/chart — the
// old local copies here were not sign-aware for currency ("$-2392" instead of "-$2.4K"), lacked the
// M/B tier, and skipped the off-the-scale clamp. Sharing them fixes those and keeps the table in sync.
import { formatRatioValue, formatSignedPct } from './cfoFormat'

type ParsedRatioTrendPoint = {
  period: string
  value: number
  year: number
  month: number
  sortKey: number
}

const averageFinite = (values: Array<number | null | undefined>): number | null => {
  const finite = values.filter((value): value is number => value != null && Number.isFinite(value))
  if (!finite.length) return null
  return finite.reduce((sum, value) => sum + value, 0) / finite.length
}

const parsedRatioTrend = (ratio: RatioCard): ParsedRatioTrendPoint[] => {
  return (ratio.trend || [])
    .map((point) => {
      const match = /^(\d{4})-(\d{2})/.exec(String(point.period || '').trim())
      const value = point.value
      if (!match || !Number.isFinite(value)) return null
      const year = Number(match[1])
      const month = Number(match[2])
      if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null
      return {
        period: point.period,
        value,
        year,
        month,
        sortKey: year * 12 + month,
      }
    })
    .filter((point): point is ParsedRatioTrendPoint => point != null)
    .sort((a, b) => a.sortKey - b.sortKey)
}

const ratioAverageByOffset = (points: ParsedRatioTrendPoint[], endOffsetFromLatest: number, months: number): number | null => {
  const endExclusive = points.length - endOffsetFromLatest
  if (endExclusive <= 0) return null
  const start = endExclusive - months
  if (start < 0) return null
  return averageFinite(points.slice(start, endExclusive).map((point) => point.value))
}

const ratioValueByOffset = (points: ParsedRatioTrendPoint[], offsetFromLatest: number): number | null => {
  const idx = points.length - 1 - offsetFromLatest
  const value = idx >= 0 ? points[idx]?.value : null
  return value != null && Number.isFinite(value) ? value : null
}

const ratioAverageYtd = (points: ParsedRatioTrendPoint[], year: number, throughMonth: number): number | null => {
  const values = points
    .filter((point) => point.year === year && point.month >= 1 && point.month <= throughMonth)
    .map((point) => point.value)
  return values.length === throughMonth ? averageFinite(values) : null
}

const ratioGrowth = (current: number | null, prior: number | null): number | null => {
  if (current == null || prior == null || !Number.isFinite(current) || !Number.isFinite(prior) || Math.abs(prior) < 0.000001) return null
  // Anchor to |prior| so a negative prior base (e.g. a negative margin ratio)
  // doesn't flip the sign of the change. Identical to `current / prior - 1` for
  // positive priors; direction-correct for negative ones.
  return (current - prior) / Math.abs(prior)
}

const ratioComparisonValues = (ratio: RatioCard, kind: string): { current: number | null; prior: number | null; deltaPct: number | null } | null => {
  const comparison = (ratio.comparisons || []).find((entry) => entry.kind === kind)
  if (!comparison) return null
  return {
    current: comparison.current,
    prior: comparison.prior,
    deltaPct: comparison.delta_pct,
  }
}

export const buildRatioComparisonRows = (ratio: RatioCard): string[][] => {
  const points = parsedRatioTrend(ratio)
  const latest = points.at(-1) || null
  const mom = ratioComparisonValues(ratio, 'mom')
  const last3 = ratioComparisonValues(ratio, 'l3m_vs_prior3m')
  const last12 = ratioComparisonValues(ratio, 'l12m_vs_prior12m')
  const windows = [
    {
      label: 'Current month',
      current: mom?.current ?? ratioValueByOffset(points, 0),
      prior: mom?.prior ?? ratioValueByOffset(points, 1),
      deltaPct: mom?.deltaPct ?? null,
    },
    {
      label: 'Last 3 months',
      current: last3?.current ?? ratioAverageByOffset(points, 0, 3),
      prior: last3?.prior ?? ratioAverageByOffset(points, 3, 3),
      deltaPct: last3?.deltaPct ?? null,
    },
    {
      label: 'YTD',
      current: latest ? ratioAverageYtd(points, latest.year, latest.month) : null,
      prior: latest ? ratioAverageYtd(points, latest.year - 1, latest.month) : null,
      deltaPct: null,
    },
    {
      label: 'Last 12 months',
      current: last12?.current ?? ratioAverageByOffset(points, 0, 12),
      prior: last12?.prior ?? ratioAverageByOffset(points, 12, 12),
      deltaPct: last12?.deltaPct ?? null,
    },
  ]
  return windows.map((window) => [
    window.label,
    formatRatioValue(window.current, ratio.unit),
    formatRatioValue(window.prior, ratio.unit),
    formatSignedPct(window.deltaPct ?? ratioGrowth(window.current, window.prior)),
  ])
}
